import alertErrorSmallTokens from '@amzn/meridian-tokens/base/icon/alert-error-small';
import alertSuccessSmallTokens from '@amzn/meridian-tokens/base/icon/alert-success-small';
import alertWarningSmallTokens from '@amzn/meridian-tokens/base/icon/alert-warning-small';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import styled from '@emotion/styled';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import {
    Button,
    Input,
    Text,
    TranslatedString,
    TranslationFunction,
    useTranslation,
} from '../../../components/blocks/localization';
import { HelpPopover } from '../../../components/constructed';
import { ADD_PRICE_LIST_ITEMS_REQUEST } from '../../../constants';
import {
    formatMaxOrderableQuantity,
    isOrderableQuantity,
    QUANTITY_INPUT_PATTERN,
} from '../../../constants/orderQuantity';
import { useItemTableHelpers } from '../../../hooks/useItemTableHelpers';
import {
    AsinToImageUrlMap,
    ColumnLayout,
    IAsynchronouslyLoadedValue,
    PoReviewRow,
    ResellerItem,
    ResellerPriceList,
} from '../../../redux/types';
import { toFamilyTranslationKey } from '../../../utils/textUtils';
import {
    candidateItemIds,
    hasRowDetail,
    ITEM_CELL,
    QUANTITY_CELL,
    UnconfirmedCell,
    unconfirmedCells,
} from '../poReviewFindings';
import { isNeutralAddedRow, PoReviewRowStatus, rowStatus } from '../poReviewValidation';
import { PoReviewItemOption, PoReviewItemSelect } from './PoReviewItemSelect';
import { PoReviewRowDetail } from './PoReviewRowDetail';

export const EMPTY_CELL = '—';
const ITEM_SELECT_WIDTH = 200;
const IDENTIFIER_SELECT_WIDTH = 150;
const SELECT_POPOVER_MAX_HEIGHT = 320;
const QUANTITY_INPUT_WIDTH = 80;

const ItemThumbnailSpacer = styled.div`
    flex-shrink: 0;
    width: 2.5em;
`;

const NO_SKU_PREFIX = 'NO SKU';
export const realSku = (item: ResellerItem): string | undefined =>
    item.externalSku && !item.externalSku.startsWith(NO_SKU_PREFIX) ? item.externalSku : undefined;

export interface PoReviewTableRow extends PoReviewRow {
    isDetail: boolean;
}

const cellMarker = (row: PoReviewRow, cell: UnconfirmedCell, dataTestIdPrefix: string, t: TranslationFunction) =>
    unconfirmedCells(row).has(cell) ? (
        <span
            role={'img'}
            aria-label={t('statusWarning-iconLabel')}
            data-testid={`${dataTestIdPrefix}.row[${row.rowId}].${cell}Unconfirmed`}
        >
            <Icon tokens={alertWarningSmallTokens} />
        </span>
    ) : null;

const STATUS_ICON: Record<PoReviewRowStatus, { tokens: object; wordKey: string }> = {
    [PoReviewRowStatus.ERROR]: { tokens: alertErrorSmallTokens, wordKey: 'statusError-word' },
    [PoReviewRowStatus.WARNING]: { tokens: alertWarningSmallTokens, wordKey: 'statusWarning-word' },
    [PoReviewRowStatus.OK]: { tokens: alertSuccessSmallTokens, wordKey: 'statusOk-word' },
};

const statusCell = (row: PoReviewRow, submitAttempted: boolean, dataTestIdPrefix: string, t: TranslationFunction) => {
    // An added line carries no status until a submit is attempted.
    if (isNeutralAddedRow(row, submitAttempted)) {
        return <span data-testid={`${dataTestIdPrefix}.row[${row.rowId}].statusIcon`} />;
    }
    const { tokens, wordKey } = STATUS_ICON[rowStatus(row)];
    return (
        <span data-testid={`${dataTestIdPrefix}.row[${row.rowId}].statusIcon`}>
            <Row spacing={'xsmall'} alignmentVertical={'center'}>
                <Icon tokens={tokens} />
                <Text>{t(wordKey)}</Text>
            </Row>
        </span>
    );
};

const removeLineCell = (
    row: PoReviewRow,
    onRemoveLine: (rowId: string) => void,
    disabled: boolean | undefined,
    dataTestIdPrefix: string,
    t: TranslationFunction
) => (
    <Button
        type={'icon'}
        size={'small'}
        disabled={disabled}
        onClick={() => onRemoveLine(row.rowId)}
        data-testid={`${dataTestIdPrefix}.row[${row.rowId}].removeLineButton`}
    >
        <Icon tokens={trashTokens}>{t('removeLine-buttonLabel')}</Icon>
    </Button>
);

export const flattenRows = (rows: PoReviewRow[]): PoReviewTableRow[] =>
    rows.flatMap((row) =>
        hasRowDetail(row)
            ? [
                  { ...row, isDetail: false },
                  { ...row, isDetail: true },
              ]
            : [{ ...row, isDetail: false }]
    );

export const tableRowKey = (row: PoReviewTableRow): string => (row.isDetail ? `${row.rowId}.detail` : row.rowId);

export const dedupedBasePriceByItemId = (priceList: ResellerPriceList | undefined): Map<string, number> => {
    const prices = new Map<string, number>();
    (priceList?.priceListItems ?? []).forEach((priceListItem) => {
        if (!prices.has(priceListItem.itemId)) {
            prices.set(priceListItem.itemId, priceListItem.basePrice);
        }
    });
    return prices;
};

// Priced off the reviewer's quantity, not the printed one, so a corrected line prices again. Undefined
// when the line has no priced item or no orderable quantity.
export const extendedPriceForRow = (basePriceByItemId: Map<string, number>, row: PoReviewRow): number | undefined => {
    const basePrice = row.itemId ? basePriceByItemId.get(row.itemId) : undefined;
    if (basePrice === undefined || !isOrderableQuantity(row.quantity)) {
        return undefined;
    }
    return basePrice * Number(row.quantity);
};

// A reseller with no active price list comes back as a list with no currency and no items.
export const hasPriceList = (priceList: ResellerPriceList | undefined): priceList is ResellerPriceList =>
    !!priceList?.currencyCode;

export const formattedEstimatedSubtotal = (
    rows: PoReviewRow[],
    priceList: ResellerPriceList | undefined
): string | undefined => {
    if (!hasPriceList(priceList)) {
        return undefined;
    }
    const basePriceByItemId = dedupedBasePriceByItemId(priceList);
    const subtotal = rows.reduce((sum: number, row) => sum + (extendedPriceForRow(basePriceByItemId, row) ?? 0), 0);
    return new Intl.NumberFormat(i18next.language, {
        style: 'currency',
        currency: priceList.currencyCode,
    }).format(subtotal);
};

export type PoReviewColumnsOptions = {
    itemMap: Map<string, ResellerItem>;
    priceList: ResellerPriceList | undefined;
    asinToImageUrls: IAsynchronouslyLoadedValue<AsinToImageUrlMap>;
    submitAttempted: boolean;
    onItemChange: (rowId: string, itemId?: string) => void;
    onQuantityChange: (rowId: string, quantity: string) => void;
    onRemoveLine: (rowId: string) => void;
    onRequestTicket: (requestType: string) => void;
    // True when the draft is shown read-only, such as a discarded draft awaiting re-open.
    disabled?: boolean;
    dataTestIdPrefix: string;
};

export type PoReviewColumn = ColumnLayout<PoReviewTableRow, keyof PoReviewTableRow>;

export function usePoReviewColumns(options: PoReviewColumnsOptions): PoReviewColumn[] {
    const {
        itemMap,
        priceList,
        asinToImageUrls,
        submitAttempted,
        onItemChange,
        onQuantityChange,
        onRemoveLine,
        onRequestTicket,
        disabled,
        dataTestIdPrefix,
    } = options;

    const { t } = useTranslation(['purchaseOrderReviewPage', 'common']);

    const { titleFormat, imageFormat } = useItemTableHelpers(itemMap, asinToImageUrls);

    const basePriceByItemId = useMemo(() => dedupedBasePriceByItemId(priceList), [priceList]);

    const optionsByColumn = useMemo(() => {
        const build = (labelFor: (item: ResellerItem) => string | undefined): PoReviewItemOption[] => {
            const options: PoReviewItemOption[] = [];
            itemMap.forEach((item) => {
                const optionLabel = labelFor(item);
                if (!optionLabel || !basePriceByItemId.has(item.itemId)) {
                    return;
                }
                const searchText = t.cognate(
                    [item.title, item.externalSku, item.upc, item.asin].filter(Boolean).join(' ')
                );
                options.push({ value: item.itemId, label: t.cognate(optionLabel), searchText });
            });
            return options.sort((left, right) => left.label.localeCompare(right.label));
        };
        return {
            item: build((item) => item.title ?? item.itemId),
            sku: build(realSku),
            upc: build((item) => item.upc),
            asin: build((item) => item.asin),
        };
    }, [itemMap, basePriceByItemId, t]);

    const unitPrice = (row: PoReviewRow) => (row.itemId ? basePriceByItemId.get(row.itemId) : undefined);

    const extendedPrice = (row: PoReviewRow) => extendedPriceForRow(basePriceByItemId, row);

    const formatPrice = (amount: number | undefined) => {
        if (!hasPriceList(priceList) || amount === undefined) {
            return EMPTY_CELL;
        }
        return new Intl.NumberFormat(i18next.language, {
            style: 'currency',
            currency: priceList.currencyCode,
        }).format(amount);
    };

    // A required field shows its red state on an extraction row always, and on an added row only once a
    // submit has been attempted.
    const showsErrorState = (row: PoReviewRow) => !isNeutralAddedRow(row, submitAttempted);
    const itemInError = (row: PoReviewRow) => !row.itemId && showsErrorState(row);
    const quantityInError = (row: PoReviewRow) => !isOrderableQuantity(row.quantity) && showsErrorState(row);

    const pinnedOptions = (row: PoReviewRow, options: PoReviewItemOption[]) => {
        const candidateIds = candidateItemIds(row);
        return options.filter((option) => candidateIds.includes(option.value));
    };

    const identifierSelect = (
        row: PoReviewTableRow,
        options: PoReviewItemOption[],
        placeholder: TranslatedString,
        testIdSuffix: string
    ) => (
        <PoReviewItemSelect
            value={row.itemId}
            onChange={(itemId) => onItemChange(row.rowId, itemId)}
            options={options}
            pinnedOptions={pinnedOptions(row, options)}
            pinnedGroupLabel={t('suggestedItems-optionGroup')}
            otherGroupLabel={t('allItems-optionGroup')}
            placeholder={placeholder}
            width={IDENTIFIER_SELECT_WIDTH}
            popoverMaxHeight={SELECT_POPOVER_MAX_HEIGHT}
            error={itemInError(row)}
            disabled={disabled}
            dataTestId={`${dataTestIdPrefix}.row[${row.rowId}].${testIdSuffix}`}
        />
    );

    return [
        {
            title: t.cognate('#'),
            uniqueId: 'lineNumber',
            help: (
                <HelpPopover
                    header={t('lineNumberHelp-header')}
                    text={t('lineNumberHelp-text')}
                    dataTestId={`${dataTestIdPrefix}.lineNumberHelp`}
                />
            ),
            format: (row) => {
                if (row.isDetail) {
                    return <PoReviewRowDetail t={t} row={row} itemMap={itemMap} />;
                }
                return (
                    <div style={{ textAlign: 'center' }}>
                        {row.isAdded ? t('addedLine-text') : String(row.itemNumber)}
                    </div>
                );
            },
            sortable: true,
        },
        {
            title: t('sku-columnTitle'),
            uniqueId: 'sku',
            format: (row) =>
                row.isDetail
                    ? null
                    : identifierSelect(row, optionsByColumn.sku, t('sku-selectPlaceholder'), 'skuSelect'),
            sortable: true,
        },
        {
            title: t('upc-columnTitle'),
            uniqueId: 'upc',
            format: (row) =>
                row.isDetail
                    ? null
                    : identifierSelect(row, optionsByColumn.upc, t('upc-selectPlaceholder'), 'upcSelect'),
            sortable: true,
        },
        {
            title: t('asin-columnTitle'),
            uniqueId: 'asin',
            format: (row) =>
                row.isDetail
                    ? null
                    : identifierSelect(row, optionsByColumn.asin, t('asin-selectPlaceholder'), 'asinSelect'),
            sortable: true,
        },
        {
            title: t('item-columnTitle'),
            uniqueId: 'item',
            help: (
                <HelpPopover header={t('itemHelp-header')} dataTestId={`${dataTestIdPrefix}.itemHelp`}>
                    <Column spacing={'small'}>
                        <Text>{t('itemHelp-text')}</Text>
                        <Link
                            type={'primary'}
                            onClick={() => onRequestTicket(ADD_PRICE_LIST_ITEMS_REQUEST)}
                            data-testid={`${dataTestIdPrefix}.itemHelpRequestLink`}
                        >
                            {t('requestPriceListItems-link')}
                        </Link>
                    </Column>
                </HelpPopover>
            ),
            format: (row) =>
                row.isDetail ? null : (
                    <Row spacing={'xsmall'} alignmentVertical={'center'}>
                        {row.itemId ? imageFormat.render!(row.itemId, row) : <ItemThumbnailSpacer />}
                        <PoReviewItemSelect
                            value={row.itemId}
                            onChange={(itemId) => onItemChange(row.rowId, itemId)}
                            options={optionsByColumn.item}
                            pinnedOptions={pinnedOptions(row, optionsByColumn.item)}
                            pinnedGroupLabel={t('suggestedItems-optionGroup')}
                            otherGroupLabel={t('allItems-optionGroup')}
                            placeholder={t('item-selectPlaceholder')}
                            width={ITEM_SELECT_WIDTH}
                            popoverMaxHeight={SELECT_POPOVER_MAX_HEIGHT}
                            error={itemInError(row)}
                            disabled={disabled}
                            dataTestId={`${dataTestIdPrefix}.row[${row.rowId}].itemSelect`}
                        />
                        {cellMarker(row, ITEM_CELL, dataTestIdPrefix, t)}
                    </Row>
                ),
            sortable: true,
        },
        {
            title: t('productDescription-columnTitle'),
            uniqueId: 'productDescription',
            format: (row) => {
                if (row.isDetail || !row.itemId) {
                    return null;
                }
                const family = itemMap.get(row.itemId)?.family;
                return (
                    <Column spacing={'xxsmall'}>
                        {family && (
                            <Text type={'b200'}>{t(toFamilyTranslationKey(family), { defaultValue: family })}</Text>
                        )}
                        {titleFormat.render!(row.itemId, row)}
                    </Column>
                );
            },
            sortable: true,
        },
        {
            title: t('quantity-columnTitle'),
            uniqueId: 'quantity',
            help: (
                <HelpPopover
                    header={t('quantityHelp-header')}
                    text={t('quantityHelp-text', { max: formatMaxOrderableQuantity(i18next.language) })}
                    dataTestId={`${dataTestIdPrefix}.quantityHelp`}
                />
            ),
            format: (row) =>
                row.isDetail ? null : (
                    <Row spacing={'xsmall'} alignmentVertical={'center'}>
                        <Input
                            value={row.quantity}
                            onChange={(quantity: string) => onQuantityChange(row.rowId, quantity)}
                            pattern={QUANTITY_INPUT_PATTERN}
                            width={QUANTITY_INPUT_WIDTH}
                            error={quantityInError(row)}
                            disabled={disabled}
                            aria-invalid={quantityInError(row) ? true : undefined}
                            aria-label={t('quantity-columnTitle')}
                            data-testid={`${dataTestIdPrefix}.row[${row.rowId}].quantityInput`}
                        />
                        {cellMarker(row, QUANTITY_CELL, dataTestIdPrefix, t)}
                    </Row>
                ),
            sortable: true,
        },
        {
            title: t('unitPrice-columnTitle'),
            uniqueId: 'unitPrice',
            format: (row) => (row.isDetail ? null : formatPrice(unitPrice(row))),
            sortable: true,
        },
        {
            title: t('extendedPrice-columnTitle'),
            uniqueId: 'extendedPrice',
            format: (row) => (row.isDetail ? null : formatPrice(extendedPrice(row))),
            sortable: true,
        },
        {
            title: t('status-columnTitle'),
            uniqueId: 'status',
            format: (row) => (row.isDetail ? null : statusCell(row, submitAttempted, dataTestIdPrefix, t)),
            sortable: true,
        },
        {
            title: t.cognate(' '),
            uniqueId: 'removeLine',
            format: (row) => (row.isDetail ? null : removeLineCell(row, onRemoveLine, disabled, dataTestIdPrefix, t)),
            sortable: false,
        },
    ];
}
